import { z } from 'zod'
import { states as getNigeriaStates } from 'nigeria-states-lgas'

// Scheduling options
export const schedulingTypeSchema = z.enum(['now', 'schedule'])

// Gender options (same as caller information)
export const genderSchema = z.enum(['male', 'female'])

// Consciousness options
export const consciousnessSchema = z.enum(['yes', 'no'])

// Oxygen requirement options
export const oxygenRequiredSchema = z.enum(['yes', 'no'])

// Severity level options
export const severityLevelSchema = z.enum(['low', 'moderate', 'high'])

const nigerianStates = getNigeriaStates()

export const stateSchema = z
  .string()
  .min(1, 'State is required')
  .refine(
    state => nigerianStates.includes(state),
    'Select a valid Nigerian state'
  )

// LGA validation (will be dynamic based on state)
export const lgaSchema = z.string().min(1, 'LGA is required')

// Removed unused schemas - using simple string validation for optional date/time fields

// Age validation
const ageSchema = z
  .number()
  .min(0, 'Age must be 0 or greater')
  .max(150, 'Age must be less than 150')

// Weight validation
const weightSchema = z
  .number()
  .min(0, 'Weight must be 0 or greater')
  .max(500, 'Weight must be less than 500 kg')

// Main ambulance request information schema
export const ambulanceRequestInformationSchema = z.object({
  // Location Information
  pickup_location: z
    .string()
    .min(1, 'Pickup location is required')
    .max(200, 'Pickup location must be less than 200 characters'),
  pickup_state: stateSchema,
  pickup_lga: lgaSchema,
  // Pickup coordinates from Google Maps (optional - populated when place is selected)
  pickup_location_latitude: z.number().optional(),
  pickup_location_longitude: z.number().optional(),

  destination: z
    .string()
    .min(1, 'Destination is required')
    .max(200, 'Destination must be less than 200 characters'),
  destination_state: stateSchema,
  destination_lga: lgaSchema,
  // Dropoff coordinates from Google Maps (optional - populated when place is selected)
  dropoff_location_latitude: z.number().optional(),
  dropoff_location_longitude: z.number().optional(),

  // Scheduling
  scheduling_type: schedulingTypeSchema,
  from_date: z.string().optional(),
  from_time: z.string().optional(),
  to_date: z.string().optional(),
  to_time: z.string().optional(),

  // Patients Information
  same_as_caller: z.boolean().default(false),
  first_name: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      'First name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  last_name: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      'Last name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  date_of_birth: z
    .union([z.string(), z.date()])
    .transform(val => {
      if (typeof val === 'string') {
        // Handle empty strings
        if (val === '' || val === null || val === undefined) {
          return undefined
        }
        const date = new Date(val)
        if (isNaN(date.getTime())) {
          throw new Error('Invalid date format')
        }
        return date
      }
      return val
    })
    .refine(
      date => !date || date <= new Date(),
      'Date of birth cannot be in the future'
    ),
  age: ageSchema,
  weight: weightSchema,
  gender: genderSchema,

  // Medical Information
  is_conscious: consciousnessSchema,
  oxygen_required: oxygenRequiredSchema,
  provisional_diagnosis: z
    .string()
    .min(1, 'Provisional diagnosis is required')
    .max(200, 'Provisional diagnosis must be less than 200 characters'),
  severity_level: severityLevelSchema,
})

// Export the inferred type
export type AmbulanceRequestInformationFormValues = z.infer<
  typeof ambulanceRequestInformationSchema
>

// Default values for the form
export const defaultAmbulanceRequestInformationValues: Partial<AmbulanceRequestInformationFormValues> =
  {
    scheduling_type: 'now',
    same_as_caller: true,
    pickup_location: 'Nigeria Breweries, Ikeja, Lagos',
    destination: 'Nigeria Breweries, Ikeja, Lagos',
    from_time: '00:00',
    to_time: '00:00',
    first_name: 'Adebayo',
    last_name: 'Adeniji',
    date_of_birth: new Date('2025-09-19'),
    age: 0,
    weight: 0,
    gender: 'male',
    is_conscious: 'yes',
    oxygen_required: 'yes',
  }

// Validation error messages
export const validationMessages = {
  required: 'This field is required',
  invalidDate: 'Invalid date format',
  futureDate: 'Date cannot be in the future',
  timeFormat: 'Time must be in HH:MM format',
  nameFormat: 'Name can only contain letters, spaces, hyphens, and apostrophes',
  maxLength: (max: number) => `Must be less than ${max} characters`,
  minValue: (min: number) => `Must be ${min} or greater`,
  maxValue: (max: number) => `Must be less than ${max}`,
} as const
